import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  memo,
} from 'react';
import MainLayout from '../../../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {tr} from '../../../../../translations';
import {getByScreenSize} from '../../../../../utils/responsive';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import TextGeneric from '../../../../../components/textGeneric';
import GenericButton from '../../../../../components/button';
import DatePicker from '../../../../../components/datePicker';
import MultiSelectPicker from '../../../../../components/multiSelectPicker';
import {convertToYYYYMMDD} from '../../../../../utils/helpers';
import RadioPicker from '../../../../../components/radioPicker';
import GenericInput from '../../../../../components/textInput';
import {FieldType, ToastType} from '../../../../../utils/enums';
import {Theme} from '../../../../../theme/lightTheme';
import {SectionList, StyleSheet, View} from 'react-native';
import {showGlobalToast} from '../../../../../connected-components/toast/actions';
import {
  CreatePatientReportBody,
  FormField2,
  FormItemType,
  PatientReportValue,
} from '../../../../../interfaces/api/content/ehr';
import {
  createPatientReport,
  getListForms,
  getPatientReport,
  updatePatientReport,
} from '../../patients/actions';
import Picker from '../../../../../components/picker';
import SearchListSkeleton from '../../../../../components/searchListSkeleton';
import EmptyList from '../../../../../components/emptyList';
import {EmptyContainer} from '../../../home';
import {AddEditPatientReportProps} from '../../../../../interfaces/screens/addEditPatientReport';
import dayjs from 'dayjs';

const FormValuesContext = React.createContext<{
  formValues: Record<string, any>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  theme: Theme;
}>({
  formValues: {},
  errors: {},
  touched: {},
  theme: {} as Theme,
});

const FormHandlersContext = React.createContext<{
  handleInputChange: (key: string, value: any) => void;
  handleBlur: (key: string) => void;
}>({
  handleInputChange: () => {},
  handleBlur: () => {},
});

// Styled components
const Container = styled.View`
  width: 100%;
  flex: 1;
  position: relative;
  padding-top: ${({theme}) => theme.text.s55 * 2}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: ${({theme}) => theme.text.s55}px;
  flex: 1;
`;

const FormContainer = styled.View`
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 10px;
  padding: ${({theme}) => theme.text.s55 * 2}px;
  shadow-opacity: 0.1;
  shadow-radius: 10px;
  shadow-color: ${({theme}) => theme.text.black};
  shadow-offset: 0px 2px;
  elevation: 2;
  flex: 1;
`;

const FormTitle = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s5}px;
  color: ${({theme}) => theme.text.title};
  width: 100%;
  text-align: center;
`;

const SubmitButtonContainer = styled.View`
  align-items: flex-end;
  margin-top: ${({theme}) => theme.text.s55}px;
`;

const SaveButton = styled(GenericButton)`
  min-width: 150px;
`;

const Separator = styled.View`
  height: 1px;
  width: 100%;
  background-color: ${({theme}) => theme.border};
  margin-vertical: ${({theme}) => theme.text.s55}px;
`;

const TextInputItem = styled(GenericInput)`
  ${({multiline, theme}) =>
    multiline &&
    `
     padding-top: 3%;
  padding-bottom: 3%;
  height: ${theme.text.s1 * 3}px;
  align-items: flex-start;
  text-align-vertical: top;
    `}
`;

// Styled components for errors
const ErrorText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.error};
  font-size: ${({theme}) => theme.text.s9}px;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const FieldWrapper = styled.View`
  margin-bottom: ${({theme}) => theme.text.s7}px;
`;

const SectionTitle = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s5}px;
  color: ${({theme}) => theme.text.dark};
  margin-bottom: ${({theme}) => theme.text.s9}px;
  margin-top: ${({theme}) => theme.text.s8}px;
`;

const TextField = memo(
  ({field}: {field: FormField2}) => {
    const {formValues, errors, touched, theme} =
      React.useContext(FormValuesContext);
    const {handleInputChange, handleBlur} =
      React.useContext(FormHandlersContext);
    const showError = errors[field.key] && touched[field.key];

    return (
      <FieldWrapper>
        <TextInputItem
          isLabelBold
          key={field.label}
          label={field?.label || ''}
          placeholder={field?.placeholder || ''}
          multiline={field.type === FieldType.MULTILINE_TEXT}
          scrollEnabled={field.type === FieldType.MULTILINE_TEXT}
          value={formValues[field.key] || ''}
          onChangeText={(val: string) => {
            // For number fields, only allow digits
            if (field.type === FieldType.NUMBER) {
              if (val === '' || /^\d+$/.test(val)) {
                handleInputChange(field?.key, val);
              }
            } else {
              handleInputChange(field?.key, val);
            }
          }}
          onBlur={() => handleBlur(field.key)}
          keyboardType={
            field.type === FieldType.NUMBER ? 'number-pad' : 'default'
          }
          containerStyle={{
            marginBottom: getByScreenSize(theme.text.s7, theme.text.s9) / 2,
          }}
          isRequired={field.is_required}
          hasError={!!showError}
          errorMessage={showError ? errors[field.key] : ''}
          enableErrorMessage={true}
        />
        {field.help_text && (
          <TextGeneric
            variant="caption"
            style={{color: theme.text.grey, marginTop: 4}}>
            {field.help_text}
          </TextGeneric>
        )}
      </FieldWrapper>
    );
  },
  (prevProps, nextProps) => prevProps.field.id === nextProps.field.id,
);

const DateField = memo(
  ({field}: {field: FormField2}) => {
    const {formValues, errors, touched} = React.useContext(FormValuesContext);
    const {handleInputChange, handleBlur} =
      React.useContext(FormHandlersContext);
    const showError = errors[field.key] && touched[field.key];

    return (
      <FieldWrapper>
        <DatePicker
          isLabelBold
          placeholder={
            field.placeholder
              ? field.placeholder
              : field.type === FieldType.DATE
                ? 'MM/DD/YYYY'
                : field.type === FieldType.TIME
                  ? 'HH:mm'
                  : 'date'
          }
          mode={
            field.type === FieldType.DATE
              ? 'date'
              : field.type === FieldType.TIME
                ? 'time'
                : 'datetime'
          }
          value={
            formValues?.[field?.key]
              ? convertToYYYYMMDD(formValues[field.key]) || undefined
              : undefined
          }
          label={field.label}
          onValueChange={value => handleInputChange(field.key, value)}
          isRequired={field.is_required}
          onBlur={() => handleBlur(field.key)}
        />
        {showError && <ErrorText>{errors[field.key]}</ErrorText>}
      </FieldWrapper>
    );
  },
  (prevProps, nextProps) => prevProps.field.id === nextProps.field.id,
);

const SelectField = memo(
  ({field}: {field: FormField2}) => {
    const theme = useTheme() as Theme;
    const {formValues, errors, touched} = React.useContext(FormValuesContext);
    const {handleInputChange} = React.useContext(FormHandlersContext);
    const showError = errors[field.key] && touched[field.key];

    return (
      <FieldWrapper>
        <RadioPicker
          key={field?.label}
          label={field?.label}
          radioButtons={(field?.options || []).map(option => ({
            id: option.value,
            label: option.label,
            description: option.description || option.help_text || '',
            containerStyle: {
              minHeight: theme.text.s1,
            },
          }))}
          onPress={val => handleInputChange(field.key, val)}
          selectedId={formValues[field.key] || ''}
          isRequired={field.is_required}
        />
        {showError && <ErrorText>{errors[field.key]}</ErrorText>}
      </FieldWrapper>
    );
  },
  (prevProps, nextProps) => prevProps.field.id === nextProps.field.id,
);

const MultipleChoiceField = memo(
  ({field}: {field: FormField2}) => {
    const theme = useTheme() as Theme;
    const {formValues, errors, touched} = React.useContext(FormValuesContext);
    const {handleInputChange, handleBlur} =
      React.useContext(FormHandlersContext);
    const showError = errors[field.key] && touched[field.key];

    return (
      <FieldWrapper>
        <MultiSelectPicker
          key={field.label}
          label={field.label}
          multiSelectButtons={(field?.options || []).map(option => ({
            id: option.value,
            label: option.label,
            description: option.description || option.help_text || '',
            containerStyle: {
              minHeight: theme.text.s1,
            },
          }))}
          onSelectedChange={value => {
            handleInputChange(field.key, value);
            handleBlur(field.key);
          }}
          selectedIds={
            Array.isArray(formValues[field.key]) ? formValues[field.key] : []
          }
          isOneSelection={false}
          isRequired={field.is_required}
        />
        {showError && <ErrorText>{errors[field.key]}</ErrorText>}
      </FieldWrapper>
    );
  },
  (prevProps, nextProps) => prevProps.field.id === nextProps.field.id,
);

// Form field renderer that handles different field types
const FormFieldItem = memo(
  ({item}: {item: FormField2}) => {
    switch (item.type) {
      case FieldType.TEXT:
      case FieldType.NUMBER:
      case FieldType.MULTILINE_TEXT:
        return <TextField field={item} />;
      case FieldType.TIME:
      case FieldType.DATE:
      case FieldType.DATETIME:
        return <DateField field={item} />;
      case FieldType.SINGLE_CHOICE:
        return <SelectField field={item} />;
      case FieldType.MULTIPLE_CHOICE:
        return <MultipleChoiceField field={item} />;
      case FieldType.BOOLEAN:
        return (
          <SelectField
            field={{
              ...item,
              options: [
                {label: 'Yes', value: 'true'},
                {label: 'No', value: 'false'},
              ],
            }}
          />
        );
      default:
        return null;
    }
  },
  (prevProps, nextProps) => {
    // Custom shouldComponentUpdate logic
    // Only re-render if the field ID changes
    return prevProps.item.id === nextProps.item.id;
  },
);

/**
 * AddEditPatientReport screen component.
 *
 * @param {any} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const AddEditPatientReport: React.FC<AddEditPatientReportProps> = ({route}) => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {patientId, report: initialReport, callback} = route?.params || {};
  const [report, setReport] = useState(initialReport);
  const [forms, setForms] = useState<FormItemType[]>([]);
  const [selectedForm, setSelectedForm] = useState<FormItemType | null>();
  const fields = selectedForm?.fields || [];
  const formTitle = selectedForm?.label || 'Please Select the Form Type First';
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const scrollRef = useRef<SectionList>(null);

  // Fetch the full report data when in edit mode
  const fetchReportData = async () => {
    if (initialReport?.id) {
      setLoading(true);
      const fullReport = await getPatientReport(initialReport.id);
      setLoading(false);

      if (fullReport) {
        setReport(fullReport);
      } else {
        showGlobalToast({
          title: 'Error',
          message: 'Failed to load report data',
          type: ToastType.ERROR,
        });
      }
    }
  };

  const initPage = async () => {
    setIsLoading(true);
    setIsError(false);
    const res = await getListForms();
    setIsLoading(false);

    if (res) {
      setForms(res);
      if (report?.form_id) {
        const idx = res.findIndex(i => i.id === report?.form_id);
        if (idx > -1) {
          setSelectedForm(res[idx]);
        }
      }
    } else {
      setIsError(true);
    }
  };

  // Add specific effect to initialize values when both report and fields are ready
  const initFormValuesFromReport = useCallback(() => {
    if (!fields.length || !report) return;

    const initialValues: Record<string, any> = {};
    const initialTouched: Record<string, boolean> = {};

    // Initialize with default empty values first
    fields.forEach(field => {
      if (field.type === FieldType.MULTIPLE_CHOICE) {
        initialValues[field.key] = [];
      } else {
        initialValues[field.key] = '';
      }
      initialTouched[field.key] = false;
    });

    // Then overlay with actual values from the report if available
    if (report.form_fields?.length) {
      report.form_fields.forEach(formField => {
        const field = fields.find(f => f.id === formField.form_field_id);
        if (field) {
          if (field.type === FieldType.MULTIPLE_CHOICE && formField.value) {
            initialValues[field.key] = Array.isArray(formField.value)
              ? formField.value
              : [formField.value];
          } else if (field.type === FieldType.BOOLEAN && formField.value) {
            initialValues[field.key] = String(formField.value);
          } else {
            initialValues[field.key] = formField.value || '';
          }
        }
      });
    }

    setFormValues(initialValues);
    setTouched(initialTouched);
    setErrors({});
  }, [fields, report]);

  // When component mounts, fetch the form data
  useEffect(() => {
    // First load the forms
    initPage();

    // When component mounts, fetch the full report data if we're in edit mode
    if (initialReport?.id) {
      fetchReportData();
    }
  }, []);

  // Call initialization when report or fields change
  useEffect(() => {
    initFormValuesFromReport();
  }, [initFormValuesFromReport]);

  // Group fields by section for better display
  const sectionedFields = useMemo(() => {
    const sections: Record<string, FormField2[]> = {};

    fields.forEach(field => {
      if (!sections[field.section]) {
        sections[field.section] = [];
      }
      sections[field.section].push(field);
    });

    // Sort sections by the lowest order field in each section
    return Object.entries(sections).sort(([, fieldsA], [, fieldsB]) => {
      const minOrderA = Math.min(...fieldsA.map(f => f.order));
      const minOrderB = Math.min(...fieldsB.map(f => f.order));
      return minOrderA - minOrderB;
    });
  }, [fields]);

  // Format sections for SectionList
  const formSections = useMemo(() => {
    return sectionedFields.map(([section, fields]) => ({
      title: section,
      data: fields.sort((a, b) => a.order - b.order),
    }));
  }, [sectionedFields]);

  // Validate a single field
  const validateField = useCallback(
    (key: string, value: any) => {
      const field = fields.find(f => f.key === key);
      if (!field) return '';

      let fieldError = '';

      // Required field validation
      if (
        field.is_required &&
        (value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0))
      ) {
        fieldError = 'This field is required';
      }

      // Number validation
      if (field.type === 'number' && value !== '') {
        if (!/^\d+$/.test(value)) {
          fieldError = 'Please enter only numbers';
        }
      }

      // Update errors state
      setErrors(prev => ({
        ...prev,
        [key]: fieldError,
      }));

      return fieldError === '';
    },
    [fields],
  );

  // Handle input change with optimized state updates
  const handleInputChange = useCallback((key: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [key]: value,
    }));

    // Clear error for the field when user starts typing
    setErrors(prev => {
      if (prev[key]) {
        return {
          ...prev,
          [key]: '',
        };
      }
      return prev;
    });
  }, []);

  // Handle field blur
  const handleBlur = useCallback(
    (key: string) => {
      setTouched(prev => ({
        ...prev,
        [key]: true,
      }));

      // Validate on blur
      validateField(key, formValues[key]);
    },
    [formValues, validateField],
  );

  // Scroll to top helper function
  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollToLocation({
        sectionIndex: 0,
        itemIndex: 0,
        animated: true,
      });
    }
  }, []);

  // Add useEffect to set initial scroll position at the top
  useEffect(() => {
    // Ensure we start at the top of the list
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollToLocation({
          sectionIndex: 0,
          itemIndex: 0,
          animated: false,
        });
      }
    }, 100);
  }, []);

  // Validate all form fields
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Mark all fields as touched
    const newTouched: Record<string, boolean> = {};
    fields.forEach(field => {
      newTouched[field.key] = true;
    });
    setTouched(newTouched);

    // Validate each field
    fields.forEach(field => {
      const value = formValues[field.key];

      // Required field validation
      if (
        field.is_required &&
        (value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0))
      ) {
        newErrors[field.key] = 'This field is required';
        isValid = false;
      }

      // Number validation
      if (field.type === FieldType.NUMBER && value !== '') {
        if (!/^\d+$/.test(value)) {
          newErrors[field.key] = 'Please enter only numbers';
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return {isValid, errors: newErrors};
  }, [fields, formValues]);

  // Create values context for form fields
  const formValuesContextValue = useMemo(
    () => ({
      formValues,
      errors,
      touched,
      theme,
    }),
    [formValues, errors, touched, theme],
  );

  // Create handlers context for form fields
  const formHandlersContextValue = useMemo(
    () => ({
      handleInputChange,
      handleBlur,
    }),
    [handleInputChange, handleBlur],
  );

  // Handle form submission
  const handleSaveReport = useCallback(async () => {
    const {isValid} = validateForm();
    if (isValid) {
      // Format data for API submission
      if (!selectedForm?.id) {
        showGlobalToast({
          title: 'Form Required',
          message: 'Please select a form type first',
          type: ToastType.ERROR,
        });
        return;
      }

      const formattedData: CreatePatientReportBody = {
        patient_id: report?.patient_id || patientId || 0, // Fallback to 0, should never happen
        form_id: selectedForm.id, // We already checked this is not undefined
        status: report?.status || 'pending',
        date: report?.date
          ? dayjs(report?.date).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        notes: report?.notes || '',
        values: fields
          .map(field => {
            const value = formValues[field.key];
            // Skip if value is empty string, empty array, null or undefined
            if (
              value === '' ||
              value === null ||
              value === undefined ||
              (Array.isArray(value) && value.length === 0)
            ) {
              return null;
            }
            return {
              form_field_id: field.id,
              value: value,
            };
          })
          .filter((item): item is PatientReportValue => item !== null), // Type-safe filter
      };

      let res = null;
      setLoading(true);
      if (report?.id) {
        res = await updatePatientReport(report?.id, formattedData);
      } else {
        res = await createPatientReport(formattedData);
      }
      setLoading(false);

      if (res) {
        showGlobalToast({
          title: report?.id ? 'Report Updated' : 'Report Created',
          message: 'Patient report was successfully saved',
          type: ToastType.SUCCESS,
        });
        callback?.();
        navigation.goBack();
      } else {
        showGlobalToast({
          title: 'Error',
          message: 'Failed to save patient report',
          type: ToastType.ERROR,
        });
      }
    } else {
      showGlobalToast({
        title: 'Validation Error',
        message: 'Please check the form for errors and try again.',
        type: ToastType.HELP,
      });
      scrollToTop();
    }
  }, [
    fields,
    formValues,
    validateForm,
    navigation,
    scrollToTop,
    report,
    patientId,
    selectedForm,
  ]);

  return (
    <MainLayout
      enableScroll={false} // Keep this false so we can handle scrolling with SectionList
      title={tr('drawer.patients')}
      backHeader
      menuHeader
      noPadding>
      <Container>
        <ContentContainer>
          <FormContainer>
            <FormTitle bold>{formTitle}</FormTitle>
            <View style={{paddingVertical: theme.text.s55}}>
              {report?.id && isLoading && (
                <SearchListSkeleton
                  numberOfItems={10}
                  height={theme.text.s1 * 2}
                  marginBottom={theme.text.s12}
                />
              )}
              {report?.id ? null : isError ? (
                <EmptyContainer>
                  <EmptyList
                    iconName={'empty'}
                    iconColor={theme.primary}
                    iconSize={theme.text.s1 * 2}
                    title={'app.error'}
                    onRefreshPress={() => initPage()}
                    showRefresh={true}
                  />
                </EmptyContainer>
              ) : isLoading ? (
                <SearchListSkeleton
                  numberOfItems={1}
                  height={theme.text.s1 * 2}
                />
              ) : (
                <Picker
                  //@ts-ignore
                  list={forms}
                  //@ts-ignore
                  labelKey="label"
                  //@ts-ignore
                  valueKey="key"
                  value={selectedForm?.label || ''}
                  label={'Select Patient Form'}
                  onValueChange={val =>
                    setSelectedForm(forms.find(f => f.key === val))
                  }
                />
              )}
            </View>
            {!selectedForm?.id ? null : loading ? (
              <TextGeneric>Loading form data...</TextGeneric>
            ) : (
              <FormValuesContext.Provider value={formValuesContextValue}>
                <FormHandlersContext.Provider value={formHandlersContextValue}>
                  <SectionList
                    ref={scrollRef}
                    sections={formSections}
                    keyExtractor={(item: FormField2) => `field-${item.id}`}
                    renderItem={({item}: {item: FormField2}) => (
                      <FormFieldItem item={item} />
                    )}
                    renderSectionHeader={({section}) => (
                      <SectionTitle bold>{section.title}</SectionTitle>
                    )}
                    SectionSeparatorComponent={() => <Separator />}
                    stickySectionHeadersEnabled={false}
                    showsVerticalScrollIndicator={true}
                    scrollEventThrottle={16}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="on-drag"
                    contentContainerStyle={{paddingBottom: 20}}
                    scrollEnabled={true} // Explicitly enable scrolling
                    style={styles.sectionList} // Add a style with flex: 1
                    // Important performance optimizations
                    removeClippedSubviews={true}
                    maxToRenderPerBatch={10}
                    updateCellsBatchingPeriod={50}
                    windowSize={10}
                    initialNumToRender={8}
                    getItemLayout={(data, index) => ({
                      length: 80,
                      offset: 80 * index,
                      index,
                    })}
                    ListFooterComponent={
                      <SubmitButtonContainer>
                        <SaveButton
                          label="Save Report"
                          onPress={handleSaveReport}
                        />
                      </SubmitButtonContainer>
                    }
                  />
                </FormHandlersContext.Provider>
              </FormValuesContext.Provider>
            )}
          </FormContainer>
        </ContentContainer>
      </Container>
    </MainLayout>
  );
};

const styles = StyleSheet.create({
  sectionList: {
    flex: 1,
  },
});

AddEditPatientReport.displayName = 'AddEditPatientReportScreen';
export default AddEditPatientReport;
